import OfflineStore from "./OfflineStore";

// ─── Constants ───────────────────────────────────────────────────
const SIZE_STRIP_REGEX = /\s+(SS|S\d+|XS|XXL|XL|Small|Medium|Large|S|M|L|\d+(\.\d+)?)$/i;
const MAX_CACHE_ENTRIES = 20;
const SEARCH_FIELDS = [
	"name", "sku", "common_sku", "handle", "description",
	"title", "keywords", "search_terms", "tags",
	"category", "family", "style",
];

/**
 * Normalize a group code by stripping trailing size suffixes.
 * @param {string} code
 * @returns {string}
 */
function normalizeGroupCode(code) {
	if (!code) return "";
	return String(code).replace(SIZE_STRIP_REGEX, "").trim();
}

/**
 * Extract searchable text from an item's attributes.
 * Returns lowercase, space-joined string of all searchable fields.
 * @param {object} attribs
 * @returns {string}
 */
function buildSearchableText(attribs) {
	if (!attribs) return "";
	const parts = [];
	for (const field of SEARCH_FIELDS) {
		const val = attribs[field];
		if (val) parts.push(String(val));
	}
	return parts.join(" ").toLowerCase();
}

/**
 * Extract word prefixes (up to 4 chars) from a string.
 * @param {string} text
 * @returns {string[]}
 */
function extractPrefixes(text) {
	if (!text) return [];
	const words = text.split(/[\s,.\-\/|;:!?'"()\[\]{}<>@#$%^&*+=~`]+/);
	const prefixes = [];
	for (const word of words) {
		if (word.length < 2) continue;
		prefixes.push(word.length < 4 ? word : word.substring(0, 4));
	}
	return prefixes;
}


// ═══════════════════════════════════════════════════════════════════
// PrecacheIndex — inverted-index engine for fast offline catalog queries
// ═══════════════════════════════════════════════════════════════════

class PrecacheIndex {
	static #instances = new Map();

	#catalogId;
	#index = null;
	#loadPromise = null;
	#resultCache = new Map();

	constructor(catalogId) {
		this.#catalogId = catalogId;
	}

	// ─── Singleton Factory ────────────────────────────────────────

	/**
	 * Get or create the PrecacheIndex for a given catalog.
	 * @param {string|number} catalogId
	 * @returns {PrecacheIndex}
	 */
	static ForCatalog(catalogId) {
		const key = String(catalogId);
		if (!PrecacheIndex.#instances.has(key)) {
			PrecacheIndex.#instances.set(key, new PrecacheIndex(catalogId));
		}
		return PrecacheIndex.#instances.get(key);
	}

	/**
	 * Clear all catalog instances from memory.
	 */
	static InvalidateAll() {
		for (const instance of PrecacheIndex.#instances.values()) {
			instance.invalidate();
		}
		PrecacheIndex.#instances.clear();
	}

	// ─── Lifecycle ────────────────────────────────────────────────

	/**
	 * Returns true if the index is loaded in memory or can be loaded from IndexedDB.
	 * @returns {Promise<boolean>}
	 */
	async isReady() {
		if (this.#index) return true;
		try {
			await this.#ensureLoaded();
			return this.#index !== null;
		} catch {
			return false;
		}
	}

	/**
	 * Returns true if the filter index has any entries (i.e., items were synced with filter data).
	 * @returns {Promise<boolean>}
	 */
	async hasFilterData() {
		await this.#ensureLoaded();
		const fi = this.#index?.filterIndex;
		return fi != null && Object.keys(fi).length > 0;
	}

	/**
	 * Get all item IDs from the index.
	 * @returns {Promise<number[]|null>} Array of item IDs or null if index not loaded
	 */
	async getAllItemIds() {
		await this.#ensureLoaded();
		if (!this.#index?.itemMeta) return null;
		return Object.keys(this.#index.itemMeta).map(Number);
	}

	/**
	 * Clear in-memory index and result cache.
	 */
	invalidate() {
		this.#index = null;
		this.#loadPromise = null;
		this.#resultCache.clear();
	}

	// ─── Index Loading ────────────────────────────────────────────

	/**
	 * Ensure the index is loaded into memory. Loads from IndexedDB on first call.
	 * Deduplicates concurrent loads via #loadPromise.
	 * @returns {Promise<void>}
	 */
	async #ensureLoaded() {
		if (this.#index) return;

		// Deduplicate concurrent load attempts
		if (this.#loadPromise) {
			await this.#loadPromise;
			return;
		}

		this.#loadPromise = this.#loadFromStore();
		try {
			await this.#loadPromise;
		} finally {
			this.#loadPromise = null;
		}
	}

	/**
	 * Load the persisted index from IndexedDB via OfflineStore.
	 * Supports sharded format (precacheIndex:*) with legacy single-blob fallback.
	 */
	async #loadFromStore() {
		try {
			const store = OfflineStore.ForCatalog(this.#catalogId);

			// Try sharded format first
			const meta = await store.GetSyncMetadata("precacheIndex:meta");
			if (meta) {
				// Validate sync generation: if the index was persisted during a
				// different sync than the current lastSyncTime, it's stale
				const currentSyncTime = await store.GetSyncMetadata("lastSyncTime");
				if (meta.syncTime && currentSyncTime && meta.syncTime !== currentSyncTime) {
					console.warn("[PrecacheIndex] Stale persisted index (index syncTime:", meta.syncTime, "current:", currentSyncTime, ") — discarding");
					return;
				}

				const [filterIndex, searchIndex, groupIndex, pageIndex, itemMeta] = await Promise.all([
					this.#loadShardedIndex(store, "precacheIndex:filterIndex"),
					this.#loadShardedIndex(store, "precacheIndex:searchIndex"),
					store.GetSyncMetadata("precacheIndex:groupIndex"),
					store.GetSyncMetadata("precacheIndex:pageIndex"),
					this.#loadItemMetaShards(store),
				]);

				if (filterIndex && itemMeta) {
					// Guard: don't overwrite if beginIncrementalBuild() already
					// replaced the index while this async load was in flight
					if (this.#index) return;
					const gi = groupIndex || {};
					const itg = {};
					for (const [grpKey, group] of Object.entries(gi)) {
						for (const memberId of group.members) itg[memberId] = grpKey;
					}
					this.#index = { filterIndex, searchIndex: searchIndex || {}, groupIndex: gi, pageIndex: pageIndex || {}, itemMeta, itemToGroup: itg };
					this.#applyPendingPageItems();
					return;
				}
			}

			// Legacy single-blob fallback
			const data = await store.GetSyncMetadata("precacheIndex");
			if (data && data.filterIndex && data.itemMeta) {
				// Guard: don't overwrite if beginIncrementalBuild() already set the index
				if (this.#index) return;
				this.#index = data;
				// Rebuild itemToGroup if missing (legacy format)
				if (!this.#index.itemToGroup) {
					this.#index.itemToGroup = {};
					for (const [grpKey, group] of Object.entries(this.#index.groupIndex || {})) {
						for (const memberId of group.members) this.#index.itemToGroup[memberId] = grpKey;
					}
				}
				this.#applyPendingPageItems();
			}
		} catch {
			this.#index = null;
		}
	}

	/**
	 * Apply any page items that were set before the index finished loading.
	 */
	#applyPendingPageItems() {
		if (!this._pendingPageItems || !this.#index) return;
		for (const [pageId, itemIds] of Object.entries(this._pendingPageItems)) {
			this.#index.pageIndex[pageId] = itemIds;
		}
		this._pendingPageItems = null;
		this.#resultCache.clear();
	}

	/**
	 * Load itemMeta sub-shards and merge into a single object.
	 * @param {OfflineStore} store
	 * @returns {Promise<object|null>}
	 */
	async #loadItemMetaShards(store) {
		const shardCount = await store.GetSyncMetadata("precacheIndex:itemMeta:_count");
		if (!shardCount) return null;

		const shardPromises = [];
		for (let i = 0; i < shardCount; i++) {
			shardPromises.push(store.GetSyncMetadata(`precacheIndex:itemMeta:${i}`));
		}
		const shards = await Promise.all(shardPromises);

		const merged = {};
		for (const shard of shards) {
			if (shard) Object.assign(merged, shard);
		}
		return Object.keys(merged).length > 0 ? merged : null;
	}

	/**
	 * Load a sharded index (filterIndex or searchIndex).
	 * Falls back to single-blob key for v1 backward compatibility.
	 * @param {OfflineStore} store
	 * @param {string} prefix - Key prefix (e.g., "precacheIndex:searchIndex")
	 * @returns {Promise<object|null>}
	 */
	async #loadShardedIndex(store, prefix) {
		const shardCount = await store.GetSyncMetadata(`${prefix}:_count`);
		if (shardCount) {
			const shardPromises = [];
			for (let i = 0; i < shardCount; i++) {
				shardPromises.push(store.GetSyncMetadata(`${prefix}:${i}`));
			}
			const shards = await Promise.all(shardPromises);
			const merged = {};
			for (const shard of shards) {
				if (shard) Object.assign(merged, shard);
			}
			return Object.keys(merged).length > 0 ? merged : null;
		}
		// v1 fallback: single-blob key
		return store.GetSyncMetadata(prefix);
	}

	// ─── LRU Result Cache ─────────────────────────────────────────

	/**
	 * Get a cached result by key object.
	 * @param {object} keyObj
	 * @returns {object|undefined}
	 */
	#cacheGet(keyObj) {
		const key = JSON.stringify(keyObj);
		if (!this.#resultCache.has(key)) return undefined;
		const value = this.#resultCache.get(key);
		// Move to end (most recently used)
		this.#resultCache.delete(key);
		this.#resultCache.set(key, value);
		return value;
	}

	/**
	 * Store a result in the LRU cache.
	 * @param {object} keyObj
	 * @param {object} value
	 */
	#cacheSet(keyObj, value) {
		const key = JSON.stringify(keyObj);
		// If already exists, delete so re-insertion moves to end
		if (this.#resultCache.has(key)) {
			this.#resultCache.delete(key);
		}
		this.#resultCache.set(key, value);
		// Evict oldest entries if over capacity
		while (this.#resultCache.size > MAX_CACHE_ENTRIES) {
			const oldest = this.#resultCache.keys().next().value;
			this.#resultCache.delete(oldest);
		}
	}

	// ─── Index Building ───────────────────────────────────────────

	/**
	 * Initialize empty index structures for incremental building.
	 * Called once at the start of a full sync before the download loop begins.
	 */
	beginIncrementalBuild() {
		this.#index = {
			filterIndex: {},
			searchIndex: {},
			groupIndex: {},
			pageIndex: {},
			itemMeta: {},
			itemToGroup: {},
		};
		this.#resultCache.clear();
	}

	/**
	 * Ingest a batch of items into the index incrementally.
	 * Called for each batch during sync download. Same logic as the inner loop
	 * of buildFromItems, but operates on the live #index object.
	 *
	 * Also extracts term dictionary words from each item's searchable text.
	 *
	 * @param {Array} items - Batch of precache item objects
	 * @returns {{ terms: string[] }} Extracted terms for the term dictionary
	 */
	ingestBatch(items) {
		if (!this.#index) {
			throw new Error("Call beginIncrementalBuild() before ingestBatch()");
		}

		const { filterIndex, searchIndex, groupIndex, itemMeta, itemToGroup } = this.#index;
		const batchTerms = [];
		const MIN_TERM_LENGTH = 3;

		for (const item of items) {
			const attribs = item.attribs || {};
			const itemId = attribs.id;
			if (itemId == null) continue;

			// --- Filter index ---
			const filters = item.filters;
			if (filters) {
				if (Array.isArray(filters)) {
					if (!filterIndex._flat) filterIndex._flat = {};
					for (const optionId of filters) {
						const optKey = String(optionId);
						if (!filterIndex._flat[optKey]) filterIndex._flat[optKey] = [];
						filterIndex._flat[optKey].push(itemId);
					}
				} else if (typeof filters === "object") {
					for (const filterKey of Object.keys(filters)) {
						if (!filterIndex[filterKey]) filterIndex[filterKey] = {};
						const value = filters[filterKey];
						const optionIds = Array.isArray(value) ? value : [value];
						for (const optionId of optionIds) {
							const optKey = String(optionId);
							if (!filterIndex[filterKey][optKey]) filterIndex[filterKey][optKey] = [];
							filterIndex[filterKey][optKey].push(itemId);
						}
					}
				}
			}

			// --- Group index ---
			const rawGroupCode = item.group || attribs.grouping || attribs.family || attribs.common_sku;
			const grp = rawGroupCode ? normalizeGroupCode(rawGroupCode) : null;

			if (grp) {
				itemToGroup[itemId] = grp;
				if (!groupIndex[grp]) {
					groupIndex[grp] = { rep: itemId, members: [itemId] };
				} else {
					groupIndex[grp].members.push(itemId);
					const currentRepMeta = itemMeta[groupIndex[grp].rep];
					const currentGo = currentRepMeta ? currentRepMeta.go : Infinity;
					const currentRepId = groupIndex[grp].rep;
					const thisGo = item.group_order ?? Infinity;

					if (thisGo < currentGo || (thisGo === currentGo && itemId < currentRepId)) {
						groupIndex[grp].rep = itemId;
					}
				}
			}

			// --- Search index (word prefix) ---
			const searchText = buildSearchableText(attribs);
			const prefixes = extractPrefixes(searchText);
			const seen = new Set();
			for (const prefix of prefixes) {
				if (seen.has(prefix)) continue;
				seen.add(prefix);
				if (!searchIndex[prefix]) searchIndex[prefix] = [];
				searchIndex[prefix].push(itemId);
			}

			// --- Item metadata ---
			itemMeta[itemId] = {
				seq: attribs.sequence || 0,
				grp: grp || null,
				go: item.group_order ?? Infinity,
				name: (attribs.name || "").toLowerCase(),
				sku: attribs.sku || "",
				price: parseFloat(attribs.retail_price) || 0,
				st: searchText,
			};

			// --- Term dictionary extraction ---
			const words = searchText
				.replace(/[^a-z0-9\s]/g, " ")
				.split(/\s+/)
				.filter(w => w.length >= MIN_TERM_LENGTH);
			for (const word of words) {
				batchTerms.push(word);
			}
		}

		return { terms: batchTerms };
	}

	/**
	 * Finalize an incremental build: deduplicate search posting lists and persist.
	 * pageIndex is populated on-demand by PageItemsCache, not during sync.
	 */
	async finalizeIncrementalBuild() {
		if (!this.#index) return;

		// Deduplicate search index posting lists
		const searchIndex = this.#index.searchIndex;
		for (const prefix of Object.keys(searchIndex)) {
			searchIndex[prefix] = [...new Set(searchIndex[prefix])];
		}

		this.#resultCache.clear();

		// Persist
		await this.#persistIndex();
	}

	/**
	 * Persist the current in-memory index to IndexedDB as typed shards.
	 * Each index component is stored separately to avoid single large blob writes.
	 */
	async #persistIndex() {
		if (!this.#index) return;
		try {
			const store = OfflineStore.ForCatalog(this.#catalogId);
			const idx = this.#index;

			// Read the current sync time to stamp the index with its generation
			const syncTime = await store.GetSyncMetadata("lastSyncTime");

			// Shard itemMeta into chunks of 10k items
			const SHARD_SIZE = 10000;
			const metaEntries = Object.entries(idx.itemMeta);
			const shardCount = Math.ceil(metaEntries.length / SHARD_SIZE) || 1;

			const itemMetaWrites = [];
			for (let i = 0; i < shardCount; i++) {
				const shard = {};
				const slice = metaEntries.slice(i * SHARD_SIZE, (i + 1) * SHARD_SIZE);
				for (const [key, value] of slice) {
					shard[key] = value;
				}
				itemMetaWrites.push(store.SetSyncMetadata(`precacheIndex:itemMeta:${i}`, shard));
			}

			// Shard searchIndex (prefix→posting-list) into 25k-entry chunks
			const SEARCH_SHARD_SIZE = 25000;
			const searchEntries = Object.entries(idx.searchIndex || {});
			const searchShardCount = Math.ceil(searchEntries.length / SEARCH_SHARD_SIZE) || 1;
			const searchWrites = [];
			for (let i = 0; i < searchShardCount; i++) {
				const shard = {};
				const slice = searchEntries.slice(i * SEARCH_SHARD_SIZE, (i + 1) * SEARCH_SHARD_SIZE);
				for (const [key, value] of slice) shard[key] = value;
				searchWrites.push(store.SetSyncMetadata(`precacheIndex:searchIndex:${i}`, shard));
			}

			// Shard filterIndex (filter→option→posting-list) into 25k-entry chunks
			const FILTER_SHARD_SIZE = 25000;
			const filterEntries = Object.entries(idx.filterIndex || {});
			const filterShardCount = Math.ceil(filterEntries.length / FILTER_SHARD_SIZE) || 1;
			const filterWrites = [];
			for (let i = 0; i < filterShardCount; i++) {
				const shard = {};
				const slice = filterEntries.slice(i * FILTER_SHARD_SIZE, (i + 1) * FILTER_SHARD_SIZE);
				for (const [key, value] of slice) shard[key] = value;
				filterWrites.push(store.SetSyncMetadata(`precacheIndex:filterIndex:${i}`, shard));
			}

			// Write all shards in parallel
			await Promise.all([
				store.SetSyncMetadata("precacheIndex:meta", {
					version: 2,
					itemCount: metaEntries.length,
					builtAt: Date.now(),
					syncTime: syncTime || null,
				}),
				store.SetSyncMetadata("precacheIndex:searchIndex:_count", searchShardCount),
				store.SetSyncMetadata("precacheIndex:filterIndex:_count", filterShardCount),
				store.SetSyncMetadata("precacheIndex:groupIndex", idx.groupIndex),
				store.SetSyncMetadata("precacheIndex:pageIndex", idx.pageIndex),
				store.SetSyncMetadata("precacheIndex:itemMeta:_count", shardCount),
				...itemMetaWrites,
				...searchWrites,
				...filterWrites,
			]);

			// Clean up old single-blob keys from v1 format
			store.SetSyncMetadata("precacheIndex:filterIndex", null).catch(() => {});
			store.SetSyncMetadata("precacheIndex:searchIndex", null).catch(() => {});

			// Clean up legacy single-blob key if it exists
			store.SetSyncMetadata("precacheIndex", null).catch(() => {});
		} catch {
			// persist failure is non-fatal; index remains in memory
		}
	}

	/**
	 * Build all index structures from a full set of items.
	 * Delegates to beginIncrementalBuild + ingestBatch + finalizeIncrementalBuild.
	 *
	 * @param {Array} allItems - Array of precache item objects (with .attribs, .filters, .group, .group_order)
	 */
	async buildFromItems(allItems) {
		this.beginIncrementalBuild();
		this.ingestBatch(allItems);
		await this.finalizeIncrementalBuild();
	}

	/**
	 * Set page→item mapping at runtime. Called by PageItemsCache after
	 * fetching page item IDs on-demand from the API.
	 *
	 * @param {string|number} pageId
	 * @param {number[]} itemIds - Ordered array of item IDs belonging to this page
	 */
	async setPageItems(pageId, itemIds) {
		await this.#ensureLoaded();
		if (!this.#index) {
			// Index not available — store pending page items for when it loads
			if (!this._pendingPageItems) this._pendingPageItems = {};
			this._pendingPageItems[pageId] = itemIds;
			return;
		}
		this.#index.pageIndex[pageId] = itemIds;
		this.#resultCache.clear();
	}

	/**
	 * Incrementally update the index with new/changed items and removed items.
	 * Called during delta sync.
	 *
	 * @param {Array} newItems - Items to add or update
	 * @param {Array} deletedIds - Item IDs to remove
	 */
	async updateFromDelta(newItems, deletedIds) {
		await this.#ensureLoaded();
		if (!this.#index) return;

		const { filterIndex, searchIndex, groupIndex, itemMeta, pageIndex, itemToGroup } = this.#index;

		// ── Remove deleted items from all indexes ──
		if (Array.isArray(deletedIds) && deletedIds.length > 0) {
			const deletedSet = new Set(deletedIds.map(Number));

			// Remove from itemToGroup
			for (const id of deletedSet) {
				delete itemToGroup[id];
			}

			// Remove from filterIndex
			for (const filterKey of Object.keys(filterIndex)) {
				if (filterKey === "_flat") {
					for (const optKey of Object.keys(filterIndex._flat)) {
						filterIndex._flat[optKey] = filterIndex._flat[optKey].filter(id => !deletedSet.has(Number(id)));
						if (filterIndex._flat[optKey].length === 0) delete filterIndex._flat[optKey];
					}
				} else {
					for (const optKey of Object.keys(filterIndex[filterKey])) {
						filterIndex[filterKey][optKey] = filterIndex[filterKey][optKey].filter(id => !deletedSet.has(Number(id)));
						if (filterIndex[filterKey][optKey].length === 0) delete filterIndex[filterKey][optKey];
					}
					if (Object.keys(filterIndex[filterKey]).length === 0) delete filterIndex[filterKey];
				}
			}

			// Remove from searchIndex
			for (const prefix of Object.keys(searchIndex)) {
				searchIndex[prefix] = searchIndex[prefix].filter(id => !deletedSet.has(Number(id)));
				if (searchIndex[prefix].length === 0) delete searchIndex[prefix];
			}

			// Remove from groupIndex
			for (const grpKey of Object.keys(groupIndex)) {
				const group = groupIndex[grpKey];
				group.members = group.members.filter(id => !deletedSet.has(Number(id)));
				if (group.members.length === 0) {
					delete groupIndex[grpKey];
				} else if (deletedSet.has(Number(group.rep))) {
					// Re-elect representative from remaining members
					let bestId = group.members[0];
					let bestGo = itemMeta[bestId]?.go ?? Infinity;
					for (const memberId of group.members) {
						const go = itemMeta[memberId]?.go ?? Infinity;
						if (go < bestGo || (go === bestGo && memberId < bestId)) {
							bestId = memberId;
							bestGo = go;
						}
					}
					group.rep = bestId;
				}
			}

			// Remove from pageIndex
			for (const pageId of Object.keys(pageIndex)) {
				pageIndex[pageId] = pageIndex[pageId].filter(id => !deletedSet.has(Number(id)));
			}

			// Remove from itemMeta
			for (const id of deletedIds) {
				delete itemMeta[Number(id)];
			}
		}

		// ── Add/update new items ──
		if (Array.isArray(newItems) && newItems.length > 0) {
			for (const item of newItems) {
				const attribs = item.attribs || {};
				const itemId = attribs.id;
				if (itemId == null) continue;

				// Remove old data for this item first (if updating)
				if (itemMeta[itemId]) {
					const oldGrp = itemMeta[itemId].grp;
					if (oldGrp && groupIndex[oldGrp]) {
						groupIndex[oldGrp].members = groupIndex[oldGrp].members.filter(id => id !== itemId);
						if (groupIndex[oldGrp].members.length === 0) {
							delete groupIndex[oldGrp];
						}
					}

					// Remove old filter postings for this item
					if (filterIndex._flat) {
						for (const optKey of Object.keys(filterIndex._flat)) {
							filterIndex._flat[optKey] = filterIndex._flat[optKey].filter(id => id !== itemId);
							if (filterIndex._flat[optKey].length === 0) delete filterIndex._flat[optKey];
						}
					} else {
						for (const fKey of Object.keys(filterIndex)) {
							const bucket = filterIndex[fKey];
							if (typeof bucket !== "object") continue;
							for (const optKey of Object.keys(bucket)) {
								bucket[optKey] = bucket[optKey].filter(id => id !== itemId);
								if (bucket[optKey].length === 0) delete bucket[optKey];
							}
							if (Object.keys(bucket).length === 0) delete filterIndex[fKey];
						}
					}

					// Remove old search index postings for this item
					const oldPrefixes = extractPrefixes(itemMeta[itemId].st || "");
					const oldSeen = new Set();
					for (const prefix of oldPrefixes) {
						if (oldSeen.has(prefix)) continue;
						oldSeen.add(prefix);
						if (searchIndex[prefix]) {
							searchIndex[prefix] = searchIndex[prefix].filter(id => id !== itemId);
							if (searchIndex[prefix].length === 0) delete searchIndex[prefix];
						}
					}
				}

				// Filter index
				const filters = item.filters;
				if (filters) {
					if (Array.isArray(filters)) {
						if (!filterIndex._flat) filterIndex._flat = {};
						for (const optionId of filters) {
							const optKey = String(optionId);
							if (!filterIndex._flat[optKey]) filterIndex._flat[optKey] = [];
							if (!filterIndex._flat[optKey].includes(itemId)) {
								filterIndex._flat[optKey].push(itemId);
							}
						}
					} else if (typeof filters === "object") {
						for (const filterKey of Object.keys(filters)) {
							if (!filterIndex[filterKey]) filterIndex[filterKey] = {};
							const value = filters[filterKey];
							const optionIds = Array.isArray(value) ? value : [value];
							for (const optionId of optionIds) {
								const optKey = String(optionId);
								if (!filterIndex[filterKey][optKey]) filterIndex[filterKey][optKey] = [];
								if (!filterIndex[filterKey][optKey].includes(itemId)) {
									filterIndex[filterKey][optKey].push(itemId);
								}
							}
						}
					}
				}

				// Group index
				const rawGroupCode = item.group || attribs.grouping || attribs.family || attribs.common_sku;
				const grp = rawGroupCode ? normalizeGroupCode(rawGroupCode) : null;

				if (grp) {
					itemToGroup[itemId] = grp;
					if (!groupIndex[grp]) {
						groupIndex[grp] = { rep: itemId, members: [itemId] };
					} else {
						if (!groupIndex[grp].members.includes(itemId)) {
							groupIndex[grp].members.push(itemId);
						}
						const currentRepGo = itemMeta[groupIndex[grp].rep]?.go ?? Infinity;
						const thisGo = item.group_order ?? Infinity;
						if (thisGo < currentRepGo || (thisGo === currentRepGo && itemId < groupIndex[grp].rep)) {
							groupIndex[grp].rep = itemId;
						}
					}
				}

				// Search index
				const searchText = buildSearchableText(attribs);
				const prefixes = extractPrefixes(searchText);
				const seen = new Set();
				for (const prefix of prefixes) {
					if (seen.has(prefix)) continue;
					seen.add(prefix);
					if (!searchIndex[prefix]) searchIndex[prefix] = [];
					if (!searchIndex[prefix].includes(itemId)) {
						searchIndex[prefix].push(itemId);
					}
				}

				// Item metadata
				itemMeta[itemId] = {
					seq: attribs.sequence || 0,
					grp: grp || null,
					go: item.group_order ?? Infinity,
					name: (attribs.name || "").toLowerCase(),
					sku: attribs.sku || "",
					price: parseFloat(attribs.retail_price) || 0,
					st: searchText,
				};
			}
		}

		this.#resultCache.clear();

		// Re-persist
		await this.#persistIndex();
	}

	// ─── Filter Query ─────────────────────────────────────────────

	/**
	 * Get item IDs matching the given filters and page scope.
	 * Returns a Set<itemId>, or null if no filters/page are active (meaning "all items").
	 *
	 * @param {Array} filters - Array of { filterId, value } objects
	 * @param {string|number|null} pageId - Page ID to scope to, or null for all pages
	 * @param {object|null} referenceMap - Map of filter key aliases (e.g., "c.35" -> "1126")
	 * @returns {Promise<Set<number>|null>}
	 */
	async getMatchingItemIds(filters, pageId, referenceMap) {
		await this.#ensureLoaded();
		if (!this.#index) return null;

		const { filterIndex, pageIndex } = this.#index;
		const hasFilters = Array.isArray(filters) && filters.some(f => {
			if (!f || f.value == null) return false;
			if (Array.isArray(f.value)) return f.value.length > 0;
			return true;
		});
		const hasPage = pageId != null && pageId !== "";

		if (!hasFilters && !hasPage) return null;

		let resultSet = null;

		// ── Page scoping ──
		if (hasPage) {
			const pageItems = pageIndex[pageId] || pageIndex[String(pageId)];
			if (pageItems) {
				resultSet = new Set(pageItems);
			} else {
				// Page items not cached yet — return null to signal fallthrough to API
				return null;
			}
		}

		// ── Filter intersection ──
		if (hasFilters) {
			// Detect format: if filterIndex has _flat, items use array format
			const isArrayFormat = filterIndex._flat != null;

			for (const filter of filters) {
				if (!filter || filter.value == null) continue;
				const values = Array.isArray(filter.value) ? filter.value : [filter.value];
				if (values.length === 0) continue;

				let filterMatches = new Set();

				if (isArrayFormat) {
					// Array format: look up each option value in _flat
					for (const val of values) {
						const optKey = String(val);
						const posting = filterIndex._flat[optKey];
						if (posting) {
							for (const id of posting) filterMatches.add(id);
						}
					}
				} else {
					// Object format: resolve the filter key
					let filterKey = filter.filterId || filter.property;

					// Try direct key first
					let filterBucket = filterIndex[filterKey];

					// If not found, try referenceMap
					if (!filterBucket && referenceMap) {
						const mapped = referenceMap[filterKey];
						if (mapped && filterIndex[mapped]) {
							filterKey = mapped;
							filterBucket = filterIndex[filterKey];
						}
					}


					if (filterBucket) {
						// OR within filter values
						for (const val of values) {
							const optKey = String(val);
							const posting = filterBucket[optKey];
							if (posting) {
								for (const id of posting) filterMatches.add(id);
							}
						}
					}
				}

				// AND across filters: intersect with current result
				if (resultSet === null) {
					resultSet = filterMatches;
				} else {
					const intersected = new Set();
					for (const id of resultSet) {
						if (filterMatches.has(id)) intersected.add(id);
					}
					resultSet = intersected;
				}
			}
		}

		return resultSet;
	}

	// ─── Search ───────────────────────────────────────────────────

	/**
	 * Search items by query text, optionally scoped to a set of item IDs.
	 * Returns an array of item IDs sorted by relevance.
	 *
	 * @param {string} query - Search query
	 * @param {Set<number>|null} scopeItemIds - If provided, only search within these IDs
	 * @returns {Promise<number[]|null>}
	 */
	async searchItems(query, scopeItemIds) {
		if (!query || !query.trim()) return null;

		await this.#ensureLoaded();
		if (!this.#index) return null;

		const { searchIndex, itemMeta } = this.#index;

		const terms = query.toLowerCase().trim()
			.split(/[\s,.\-\/|;:!?'"()\[\]{}<>@#$%^&*+=~`\n]+/)
			.filter(t => t.length > 0);

		if (terms.length === 0) return null;

		// Union (OR) across terms — gather all items matching any term prefix.
		// Scoring below ensures items matching more/all terms rank highest.
		let candidates = new Set();

		for (const term of terms) {
			const prefix = term.length < 4 ? term : term.substring(0, 4);
			const posting = searchIndex[prefix];

			if (posting) {
				for (const id of posting) candidates.add(id);
			}
		}

		if (candidates.size === 0) return [];

		// Intersect with scope if provided
		if (scopeItemIds) {
			const scoped = new Set();
			for (const id of candidates) {
				if (scopeItemIds.has(id)) scoped.add(id);
			}
			candidates = scoped;
		}

		if (candidates.size === 0) return [];

		// Pre-compile word-boundary regexes once per term (avoids 30k+ compilations)
		const termRegexes = terms.map(term => {
			const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
			return new RegExp(`\\b${escaped}\\b`);
		});

		// Re-score candidates with weighted field scoring.
		// SKU/name matches rank higher than description/category matches.
		const scored = [];
		for (const id of candidates) {
			const meta = itemMeta[id];
			if (!meta) continue;

			const st = meta.st;
			const sku = meta.sku ? meta.sku.toLowerCase() : "";
			let score = 0;

			for (let i = 0; i < terms.length; i++) {
				const term = terms[i];
				if (!st.includes(term)) continue;

				score += 1; // Base match

				// Whole word match bonus
				if (termRegexes[i].test(st)) {
					score += 0.5;
				}

				// SKU match (highest weight — most specific identifier)
				if (sku && sku.includes(term)) {
					score += sku === term ? 5 : 3;
				}

				// Name match (high weight — primary display field)
				if (meta.name.includes(term)) {
					score += 2;
				}
			}

			// All-terms bonus
			if (terms.length > 1 && score > 0) {
				const allMatch = terms.every(t => st.includes(t));
				if (allMatch) score += 10 * terms.length;
			}

			if (score > 0) {
				scored.push({ id, score });
			}
		}

		scored.sort((a, b) => b.score - a.score);
		return scored.map(s => s.id);
	}

	// ─── Filter Counts ────────────────────────────────────────────

	/**
	 * Compute cascading filter counts for each filter option.
	 * Deep copies filterDefs and adds `count` to each option.
	 *
	 * @param {Array} filterDefs - Array of filter definition objects with .values arrays
	 * @param {Set<number>|null} scopeItemIds - Current scope (page + search narrowed)
	 * @param {Array} activeFilters - Currently active filters [{ filterId, value }]
	 * @param {object|null} referenceMap - Filter key alias map
	 * @returns {Promise<Array|null>}
	 */
	async getFilterCounts(filterDefs, scopeItemIds, activeFilters, referenceMap) {
		if (!Array.isArray(filterDefs)) return null;

		await this.#ensureLoaded();
		if (!this.#index) return null;

		const { filterIndex, itemMeta } = this.#index;

		// Deep copy
		const result = JSON.parse(JSON.stringify(filterDefs));

		// Detect format
		const isArrayFormat = filterIndex._flat != null;

		// Build active filter map for cascading logic
		const activeMap = new Map();
		if (Array.isArray(activeFilters)) {
			for (const f of activeFilters) {
				if (!f || f.value == null) continue;
				const vals = Array.isArray(f.value) ? f.value : [f.value];
				if (vals.length > 0) {
					activeMap.set(f.filterId || f.property, vals);
				}
			}
		}

		for (const filterDef of result) {
			if (!Array.isArray(filterDef.values)) continue;

			const filterKey = filterDef.filterId || filterDef.property || filterDef.reference || filterDef.id;

			// For cascading: compute scope excluding THIS filter from activeFilters
			let cascadeScope = scopeItemIds;
			if (activeMap.size > 0) {
				// Apply all OTHER active filters to narrow scope
				for (const [otherKey, otherValues] of activeMap) {
					if (otherKey === filterKey) continue; // Skip self

					let otherMatches = new Set();

					if (isArrayFormat) {
						for (const val of otherValues) {
							const posting = filterIndex._flat?.[String(val)];
							if (posting) {
								for (const id of posting) otherMatches.add(id);
							}
						}
					} else {
						let resolvedKey = otherKey;
						let bucket = filterIndex[resolvedKey];
						if (!bucket && referenceMap) {
							const mapped = referenceMap[resolvedKey];
							if (mapped && filterIndex[mapped]) {
								resolvedKey = mapped;
								bucket = filterIndex[resolvedKey];
							}
						}
						if (bucket) {
							for (const val of otherValues) {
								const posting = bucket[String(val)];
								if (posting) {
									for (const id of posting) otherMatches.add(id);
								}
							}
						}
					}

					// Intersect
					if (cascadeScope === null) {
						cascadeScope = otherMatches;
					} else {
						const intersected = new Set();
						for (const id of cascadeScope) {
							if (otherMatches.has(id)) intersected.add(id);
						}
						cascadeScope = intersected;
					}
				}
			}

			// Count each option within the cascade scope
			for (const option of filterDef.values) {
				const optionId = option.id ?? option.value;
				if (optionId == null) {
					option.count = 0;
					continue;
				}

				let posting = null;

				if (isArrayFormat) {
					posting = filterIndex._flat?.[String(optionId)];
				} else {
					let resolvedKey = filterKey;
					let bucket = filterIndex[resolvedKey];
					if (!bucket && referenceMap) {
						const mapped = referenceMap[resolvedKey];
						if (mapped && filterIndex[mapped]) {
							resolvedKey = mapped;
							bucket = filterIndex[resolvedKey];
						}
					}
					if (bucket) {
						posting = bucket[String(optionId)];
					}
				}

				if (!posting) {
					option.count = 0;
					continue;
				}

				if (cascadeScope === null) {
					// No scope restriction — count all items in posting
					option.count = posting.length;
				} else {
					let count = 0;
					for (const id of posting) {
						if (cascadeScope.has(id)) count++;
					}
					option.count = count;
				}
			}
		}

		return result;
	}

	// ─── Grouping ─────────────────────────────────────────────────

	/**
	 * Replace grouped item IDs with their representative.
	 * Items not in any group pass through as-is.
	 *
	 * @param {number[]} itemIds - Array of item IDs
	 * @param {boolean} [useInputOrder=false] - When true (search active), use the
	 *   first encountered member as representative instead of the pre-built one.
	 *   This preserves search relevance — the highest-scoring member is shown.
	 * @returns {Promise<{ids: number[], groupCounts: Object<number,number>}|null>}
	 */
	async applyGrouping(itemIds, useInputOrder = false) {
		if (!Array.isArray(itemIds)) return null;

		await this.#ensureLoaded();
		if (!this.#index) return null;

		const { groupIndex, itemMeta, itemToGroup } = this.#index;

		// Build set of filtered IDs for membership checks
		const inputSet = new Set(itemIds);
		const seen = new Set();
		const ids = [];
		const groupCounts = {};
		const groupPriceRanges = {};
		const groupFilteredMembers = {}; // repId → [filtered member IDs]

		for (const id of itemIds) {
			const grpKey = itemToGroup[id];
			if (grpKey) {
				if (seen.has(grpKey)) continue;
				seen.add(grpKey);

				// Use pre-built rep only if it's in the filtered set.
				// Otherwise use the current filtered member.
				// When searching, always use the first encountered (highest relevance).
				const prebuiltRep = groupIndex[grpKey].rep;
				const repId = (useInputOrder || !inputSet.has(prebuiltRep)) ? id : prebuiltRep;
				ids.push(repId);

				// Collect only group members that are in the filtered set
				const allMembers = groupIndex[grpKey].members;
				const filteredMembers = allMembers.filter(m => inputSet.has(m));
				groupCounts[repId] = filteredMembers.length;
				groupFilteredMembers[repId] = filteredMembers;

				// Compute price range across filtered group members only
				if (filteredMembers.length > 1) {
					let min = Infinity, max = -Infinity;
					for (const mid of filteredMembers) {
						const p = itemMeta[mid]?.price;
						if (p != null && p > 0) {
							if (p < min) min = p;
							if (p > max) max = p;
						}
					}
					if (min !== Infinity) {
						groupPriceRanges[repId] = min === max ? min : `${min} - ${max}`;
					}
				}
			} else {
				ids.push(id);
			}
		}

		return { ids, groupCounts, groupPriceRanges, groupFilteredMembers };
	}

	// ─── Sorting ──────────────────────────────────────────────────

	/**
	 * Sort item IDs by metadata field.
	 *
	 * @param {number[]} itemIds - Array of item IDs to sort
	 * @param {string|null} sort - Sort string: "field:direction" or "field" (default asc)
	 * @returns {Promise<number[]|null>}
	 */
	async sortItemIds(itemIds, sort) {
		if (!Array.isArray(itemIds)) return null;

		await this.#ensureLoaded();
		if (!this.#index) return null;

		const { itemMeta } = this.#index;

		// Map field names to metadata keys
		const fieldMap = {
			name: "name",
			sku: "sku",
			retail_price: "price",
			price: "price",
			sequence: "seq",
		};

		const sorted = [...itemIds];

		if (!sort) {
			// Default: sequence (non-zero first, ascending), then by id
			sorted.sort((a, b) => {
				const metaA = itemMeta[a];
				const metaB = itemMeta[b];
				const seqA = metaA?.seq || 0;
				const seqB = metaB?.seq || 0;
				if (seqA > 0 && seqB > 0) return seqA - seqB;
				if (seqA > 0 && seqB === 0) return -1;
				if (seqA === 0 && seqB > 0) return 1;
				return a - b;
			});
			return sorted;
		}

		const parts = sort.includes(":") ? sort.split(":") : [sort, "asc"];
		const field = parts[0];
		const direction = parts[1] || "asc";
		const dir = direction === "desc" ? -1 : 1;
		const metaKey = fieldMap[field] || field;

		sorted.sort((a, b) => {
			const metaA = itemMeta[a];
			const metaB = itemMeta[b];
			const aVal = metaA?.[metaKey];
			const bVal = metaB?.[metaKey];

			if (aVal == null && bVal == null) return 0;
			if (aVal == null) return dir;
			if (bVal == null) return -dir;
			if (typeof aVal === "string") return dir * aVal.localeCompare(bVal);
			return dir * (aVal - bVal);
		});

		return sorted;
	}
}

export default PrecacheIndex;
